import {
  ErrorInfo,
  LocalizedMessage,
} from "@buf/googleapis_googleapis.bufbuild_es/google/rpc/error_details_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { ConnectError } from "@connectrpc/connect";
import {
  getSubscriptionIdWithSignature,
  readResourceWithSignature,
  writeResource,
} from "@pocketsign/in-app-sdk";
import * as jose from "jose";
import {
  type FieldPath,
  Violations,
} from "schema/gen/es/buf/validate/validate_pb";
import type { Tenant } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  INVITATION_END_DATE,
  INVITATION_START_DATE,
  MARUMORI_PAYMENT_ENABLE_DATE,
  getProfileResourceIds,
  getSubscriptionIdResourceId,
} from "../config";
import { type TenantUIConfig, getTenantUIConfig } from "../tenant-config";
import { createAccountClient } from "./client";
import { getCurrentTime } from "./date";
import { AppError, ERROR_CODES } from "./errors";
import { t } from "./i18n";
import { sdk } from "./sdk";

/**
 * バックエンドが errors.WithHint で付けた拒否理由を取り出す。
 *
 * errorhandler は message をサニタイズして code 文字列だけにし、理由は
 * LocalizedMessage(ja-JP) detail に載せる。理由が付いているかどうかで
 * 「想定内の拒否」と「想定外の失敗」を切り分けられる。
 */
export function findLocalizedErrorMessage(
  err: ConnectError,
): string | undefined {
  return err.findDetails(LocalizedMessage).find((i) => i.locale === "ja-JP")
    ?.message;
}

export function extractConnectErrorMessage(err: ConnectError) {
  return findLocalizedErrorMessage(err) ?? err.message;
}

type ValidationMessageOptions = {
  fieldMessages?: Record<string, string>;
  fieldLabels?: Record<string, string>;
};

export function extractValidationViolationMessages(
  err: ConnectError,
  options: ValidationMessageOptions = {},
) {
  const violations = err.findDetails(Violations).flatMap((v) => v.violations);
  const messages = violations.map((violation) => {
    const fieldPath = formatValidationFieldPath(violation.field);
    const fieldMessage = fieldPath
      ? options.fieldMessages?.[fieldPath]
      : undefined;
    if (fieldMessage) {
      return fieldMessage;
    }

    const fieldLabel = fieldPath ? options.fieldLabels?.[fieldPath] : undefined;
    if (fieldLabel) {
      return t("{field}を確認してください。", { field: fieldLabel });
    }

    return violation.message || t("入力内容を確認してください。");
  });

  return [...new Set(messages)];
}

function formatValidationFieldPath(field?: FieldPath) {
  return (
    field?.elements
      .map((element) => element.fieldName ?? element.fieldNumber?.toString())
      .filter((element): element is string => !!element)
      .join(".") ?? ""
  );
}

export function isGMOPGMemberNotFoundError(err: ConnectError) {
  const gmoError = getGMOPGErrorInfo(err);
  return (
    gmoError?.detail?.toLowerCase().includes("member does not exist") ?? false
  );
}

export type GMOPGErrorInfo = {
  status: string;
  detail?: string;
  title?: string;
};

export function getGMOPGErrorInfo(err: ConnectError): GMOPGErrorInfo | null {
  const info = err
    .findDetails(ErrorInfo)
    .find((info) => info.domain === "gmopg");
  if (!info) {
    return null;
  }

  return {
    status: info.metadata.gmo_status ?? "",
    detail: info.metadata.gmo_detail || undefined,
    title: info.metadata.gmo_title || undefined,
  };
}

export function getGMOPGErrorInfoFromUnknown(
  error: unknown,
): GMOPGErrorInfo | null {
  if (!(error instanceof ConnectError)) {
    return null;
  }
  return getGMOPGErrorInfo(error);
}

export async function login(tenant: TenantWithFeature) {
  const subscriptionIdResourceId = getSubscriptionIdResourceId(tenant.slug);
  const subscriptionIdWithSignature = await getSubscriptionIdWithSignature(sdk);
  const profileResourceIds = getProfileResourceIds(tenant.slug);
  const accountClient = createAccountClient();

  if (subscriptionIdWithSignature.result !== "success") {
    throw new AppError(ERROR_CODES.FAILED_TO_GET_SUBSCRIPTION_ID, undefined, {
      sdkErrorNo: subscriptionIdWithSignature.errno,
    });
  }

  const subscriptionId = jose.decodeJwt<{ subscription_id: string }>(
    subscriptionIdWithSignature.subscriptionId,
  ).subscription_id;

  const subscriptionIdResource = await writeResource(sdk, {
    resourceId: subscriptionIdResourceId,
    resource: { value: JSON.stringify(subscriptionId) },
  });

  if (subscriptionIdResource.result !== "success") {
    const message = (
      subscriptionIdResource.data as {
        hint?: string;
        message?: string;
      }
    ).message;

    throw new AppError(ERROR_CODES.FAILED_TO_WRITE_SUBSCRIPTION_ID, message, {
      sdkErrorNo: subscriptionIdResource.errno,
    });
  }

  const profileResources = await Promise.all(
    profileResourceIds.map(async (resourceId) => ({
      id: resourceId,
      value: await readResourceWithSignature(sdk, { resourceId }),
    })),
  );

  for (const resource of profileResources) {
    if (resource.value.result !== "success") {
      throw new AppError(ERROR_CODES.PROFILE_READ_FAILED, undefined, {
        sdkErrorNo: resource.value.errno,
      });
    }
  }

  const loginResponse = await accountClient
    .login({
      subscriptionId: subscriptionIdWithSignature.subscriptionId,
      profiles: profileResources.map((resource) => {
        // ここでのチェックは不要だが、型エラーを回避するため
        if (resource.value.result !== "success") {
          throw new AppError(ERROR_CODES.PROFILE_READ_FAILED);
        }

        return {
          id: resource.id,
          value: resource.value.value,
        };
      }),
    })
    .catch((e) => {
      throw new AppError(ERROR_CODES.CONNECT_LOGIN_FAILED, e.message, {
        originalError: e,
      });
    });

  return loginResponse;
}

export type TenantWithFeature = PlainMessage<Tenant> & {
  feature: {
    invitation: boolean;
    lottely: boolean;
    storeList: boolean;
    payment: boolean;
    charge: boolean;
    transfer: boolean;
  };
  ui: TenantUIConfig;
};

export async function isInvitationEnabled(tenant: Tenant) {
  if (tenant.slug === "demo") {
    return true;
  }

  if (tenant.slug === "miyagi") {
    const currentTime = await getCurrentTime();

    if (currentTime.date < INVITATION_START_DATE) {
      return false;
    }

    if (currentTime.date > INVITATION_END_DATE) {
      return false;
    }

    return true;
  }

  return false;
}

async function isPaymentEnabled(tenant: Tenant) {
  // まるもりテナントの場合、2025-10-01まで決済機能を無効化
  if (tenant.slug === "marumori") {
    const currentTime = await getCurrentTime();

    if (currentTime.date < MARUMORI_PAYMENT_ENABLE_DATE) {
      return false;
    }
  }

  if (tenant.slug === "shiga-otsu") {
    return false;
  }

  return true;
}

const chargeEnabledTenantSlugs = new Set(["demo", "miyagi"]);

export function isChargeEnabled(tenant: Pick<Tenant, "slug">) {
  return chargeEnabledTenantSlugs.has(tenant.slug);
}

export function isTransferEnabled(tenant: Pick<Tenant, "slug">) {
  return tenant.slug !== "kyoto-kyoto";
}

function isStoreListEnabled(tenant: Tenant) {
  if (tenant.slug === "shiga-otsu") {
    return false;
  }
  return true;
}

export async function getTenantWithFeature() {
  const accountClient = createAccountClient();

  const { tenant } = await accountClient.getTenant({}).catch((error) => {
    throw new AppError(ERROR_CODES.CONNECT_GET_TENANT_FAILED, error.message, {
      originalError: error,
    });
  });

  if (!tenant) {
    throw new AppError(ERROR_CODES.TENANT_NOT_FOUND);
  }

  // テナントの機能有無をここで設定する
  const tenantWithFeature: TenantWithFeature = {
    ...tenant,
    feature: {
      invitation: await isInvitationEnabled(tenant),
      lottely: true,
      storeList: isStoreListEnabled(tenant),
      payment: await isPaymentEnabled(tenant),
      charge: isChargeEnabled(tenant),
      transfer: isTransferEnabled(tenant),
    },
    ui: getTenantUIConfig(tenant.slug),
  };

  return tenantWithFeature;
}
